import { useState, type FormEvent } from 'react';
import { errorMessage } from '../../api/client';
import {
  useDomainToolsCheck,
  useEmailCredentials,
  useToolsAddWhitelistEntries,
  useToolsCreateMailbox,
  useToolsRemoveWhitelistEntries,
} from '../../api/email';
import type { CredentialsCheck, DomainCheck } from '../../api/types';
import { can, useMe } from '../../auth/AuthContext';
import {
  Advisories,
  checkSummary,
  CreateMailboxForm,
  DnsTodo,
  MailboxesPanel,
  WhitelistPanel,
} from '../../components/email/EmailDomain';
import { EmptyState, SkeletonTable } from '../../components/states';
import { Alert, Badge, Button, Card, PageHeader, TextField, formatDate } from '../../components/ui';

interface Notice {
  kind: 'success' | 'info' | 'error';
  text: string;
}

/**
 * /staff/email-lab — one domain at a time, by name, no tenant needed.
 *
 * The bench for validating the email stack against the real providers: are
 * the four credentials right, what does this domain still need in Cloudflare,
 * is it on the mail server, create a mailbox on it, let a sender past the
 * spam filter. It talks to the staff-only `/email/*` routes, which take the
 * domain as typed and consult no tenant mapping — a domain can be checked and
 * set up here first and assigned to a tenant later.
 */
export function EmailLab() {
  const me = useMe();
  const canManage = can(me, 'provisioning:manage');
  const [draft, setDraft] = useState('');
  const [domain, setDomain] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const credentials = useEmailCredentials({ enabled: false });
  const check = useDomainToolsCheck(domain);

  if (!can(me, 'provisioning:read')) {
    return (
      <>
        <PageHeader eyebrow="Staff" title="Email lab" />
        <EmptyState
          title="Provisioning staff only"
          body="This page needs the provisioning:read permission."
        />
      </>
    );
  }

  const notify = (text: string, kind: Notice['kind'] = 'success'): void => {
    setNotice({ kind, text });
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const next = draft.trim().toLowerCase();
    if (!next) return;
    setNotice(null);
    setDomain(next);
  };

  return (
    <>
      <PageHeader
        eyebrow="Staff"
        title="Email lab"
        description="Check any domain against Cloudflare and the mail server, create a mailbox on it, manage the sender allow-list. No tenant needed — assign the domain to one later."
      />

      <Card
        title="Credentials"
        subtitle="One read against each provider; nothing is changed."
        actions={
          <Button
            variant="secondary"
            loading={credentials.isFetching}
            onClick={() => void credentials.refetch()}
          >
            Check credentials
          </Button>
        }
      >
        {credentials.error ? <Alert kind="error">{errorMessage(credentials.error)}</Alert> : null}
        {credentials.data ? (
          <CredentialsView check={credentials.data} />
        ) : (
          <p className="muted">
            Confirms the Cloudflare token and the three MXroute values the API was started with are
            accepted. Run this first after setting them.
          </p>
        )}
      </Card>

      <Card title="Domain">
        <form className="lab__row" onSubmit={submit}>
          <TextField
            label="Domain"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="acme.com"
            autoComplete="off"
            required
          />
          <Button type="submit" loading={check.isFetching}>
            Check
          </Button>
        </form>
        {notice ? <Alert kind={notice.kind}>{notice.text}</Alert> : null}
        {domain && check.isError ? <Alert kind="error">{errorMessage(check.error)}</Alert> : null}
        {domain && check.isPending && !check.isError ? <SkeletonTable rows={3} cols={4} /> : null}
        {check.data ? (
          <DomainResult
            data={check.data}
            fetching={check.isFetching}
            onRecheck={() => void check.refetch()}
            canManage={canManage}
            onChanged={notify}
          />
        ) : null}
      </Card>
    </>
  );
}

/** Both sides, pass or fail, with the library's code and message when one failed. */
function CredentialsView({ check }: { check: CredentialsCheck }) {
  const side = (
    name: string,
    result: { ok: boolean; status?: string; error?: { code: string; message: string } },
  ) => (
    <li>
      <Badge tone={result.ok ? 'ok' : 'bad'}>
        {name} {result.ok ? 'OK' : 'rejected'}
      </Badge>
      {result.status ? <span className="muted"> status {result.status}</span> : null}
      {result.error ? (
        <span className="muted">
          {' '}
          {result.error.code}: {result.error.message}
        </span>
      ) : null}
    </li>
  );
  return (
    <ul className="mailbox-list">
      {side('Cloudflare token', check.cloudflare)}
      {side('MXroute key', check.mxroute)}
    </ul>
  );
}

/** The checked domain: status, the DNS to-do list, its mailboxes and the allow-list. */
function DomainResult({
  data,
  fetching,
  onRecheck,
  canManage,
  onChanged,
}: {
  data: DomainCheck;
  fetching: boolean;
  onRecheck: () => void;
  canManage: boolean;
  onChanged: (text: string, kind?: Notice['kind']) => void;
}) {
  const create = useToolsCreateMailbox();
  const add = useToolsAddWhitelistEntries();
  const remove = useToolsRemoveWhitelistEntries();
  const summary = checkSummary(data);

  return (
    <>
      <div className="lab__summary">
        <Badge tone={summary.tone}>{summary.label}</Badge>
        <span className="muted">
          <code>{data.domain}</code> · checked {formatDate(data.checkedAt)}
        </span>
        <Button variant="secondary" loading={fetching} onClick={onRecheck}>
          Check again
        </Button>
      </div>
      <p className="muted">{summary.hint}</p>
      <DnsTodo check={data} />
      <Advisories check={data} />
      <MailboxesPanel check={data}>
        {canManage ? (
          <CreateMailboxForm
            domain={data.domain}
            onCreate={(input) => create.mutateAsync({ ...input, domain: data.domain })}
            onChanged={onChanged}
          />
        ) : (
          <p className="muted">Creating a mailbox needs the provisioning:manage permission.</p>
        )}
      </MailboxesPanel>
      <WhitelistPanel
        whitelist={data.whitelist}
        onAdd={
          canManage ? (entries) => add.mutateAsync({ domain: data.domain, entries }) : undefined
        }
        onRemove={
          canManage ? (entries) => remove.mutateAsync({ domain: data.domain, entries }) : undefined
        }
        onChanged={onChanged}
      />
    </>
  );
}
